import { AdminRole } from "@redotech/admin-sdk/admin-roles";
import { useHandler } from "@redotech/react-util/hook";
import { localStore } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface Auth {
  expiration: Temporal.Instant;
  email: string;
  token: string;
  role: AdminRole;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

export type SetAuth = (auth: Auth | undefined) => void;

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

const authStore = localStore("redo.admin_auth_token");

export function parseAuth(token: string): Auth | undefined {
  try {
    const decoded = jwtDecode<any>(token);
    const email = decoded.eml ?? decoded.sub;
    if (!email || typeof email !== "string") {
      throw new TypeError("Not a valid Auth object");
      // Do not print sensitive information - only uncomment this to test locally
      // throw new TypeError(`Not a valid Auth object: ${JSON.stringify(decoded)}`);
    }
    // Default to admin for backward compatibility with old tokens
    const role: AdminRole = decoded.adminServerRole ?? "admin";
    return {
      expiration: Temporal.Instant.fromEpochSeconds(decoded.exp),
      email,
      token,
      role,
    };
  } catch (error) {
    console.error("Failed to parse token", error);
    // Do not print sensitive information - only uncomment this to test locally
    // console.error(`Failed to parse invalid token ${token}`, error);
    return undefined;
  }
}

/** @returns whether the auth is expired (or expires within the next second) */
function isExpired(expiration: Temporal.Instant): boolean {
  const duration = expiration.since(Temporal.Now.instant());
  // Give ourselves 1 second leeway so it doesn't expire while trying to load the page
  return (
    Temporal.Duration.compare(
      Temporal.Duration.from({ seconds: 1 }),
      duration,
    ) > 0
  );
}

export const AuthRequired = memo(function AuthRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useLayoutEffect(() => {
    if (!auth) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  return auth ? <>{children}</> : null;
});

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [auth, setAuth] = useState<Auth | undefined>(() => {
    const token = authStore.get();
    if (!token) {
      return undefined;
    }
    const parsed = parseAuth(token);
    // Delete token if invalid
    if (!parsed) {
      return undefined;
    }
    // Delete token if expired
    if (isExpired(parsed.expiration)) {
      return undefined;
    }
    return parsed;
  });

  useEffect(() => {
    // Clear the local storage if the auth is undefined
    if (!auth) {
      authStore.set(undefined);
      return;
    }

    // Delete the auth when it expires
    const duration = auth.expiration.since(Temporal.Now.instant());
    const timeout = setTimeout(
      () => setAuth(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth]);

  const setAuth_ = useHandler<SetAuth>((auth) => {
    if (auth) {
      authStore.set(auth.token);
    }
    setAuth(auth);
  });

  return (
    <AuthContext.Provider value={auth}>
      <SetAuthContext.Provider value={setAuth_}>
        {children}
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});
